import React from 'react';
import { OverlayModal } from '../../../../../shared/components/overlay-modal';
import { useStyles } from './style';
import { FastField, Field, FieldArray, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { ThemedButton } from '../../../../../shared/components/themed-button';
import { FormikDropdown } from '../../../../../shared/components/formik-dropdown';
import clsx from 'clsx';
import { FormikInput } from '../../../../../shared/components/formik-input';
import { CommonSwitch } from '../../../../../shared/components/common-switch';
import { deliveryTypeOptions } from '../../../../../shared/constants/deliveryType';
import SvgInfo from '../../../../../assets/icons/Info';
import { Chip, InputAdornment } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { DaySettings } from '../day-settings';
import { CustomTimePicker } from '../../../../../shared/components/time-picker';
import * as Yup from 'yup';
import { dateToString, stringToDate } from '../../../../../shared/helpers/stringHelper';
import { FreightRule } from '../../../../../api/freight-rule/types';
import { useCreateFreightRuleMutation, useGetFreightRuleQuery, useUpdateFreightRuleMutation } from '../../../../../api/freight-rule';
import { ToastService } from '../../../../../shared/services/toastService';

interface Props {
  isModalOpen: boolean;
  closeModal: () => void;
  recordId: number | null;
}

const deliveryDays = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' },
];

export const FreightRuleForm: React.FC<Props> = ({ isModalOpen, closeModal, recordId }) => {
  const classes = useStyles();
  const [createFreightRule] = useCreateFreightRuleMutation();
  const [updateFreightRule] = useUpdateFreightRuleMutation();

  const { data: editData } = useGetFreightRuleQuery(recordId!, {
    skip: !recordId,
  });

  const schema = Yup.object().shape({
    name: Yup.string().required('Required field'),
    delivery_type: Yup.string().required('Required field'),
  });

  const onFreightRuleSave = async (
    values: FreightRule,
    formikHelpers: FormikHelpers<FreightRule>
  ) => {
    if (values.id) {
      // ✅ Update if ID exists
      await updateFreightRule(values).unwrap();
      ToastService.success('Freight rule updated successfully');
    } else {
      // ✅ Otherwise create new
      await createFreightRule(values).unwrap();
      ToastService.success('Freight rule created successfully');
    }

    formikHelpers.setSubmitting(false);
    closeModal();
  };

  const handleChipClick = (
    dayId: string,
    values: FreightRule,
    setFieldValue: (field: string, value: any) => void
  ) => {
    const existing = values.freight_day_settings_attributes || [];
    const existingIndex = existing.findIndex((d) => d.day_of_week === dayId);

    if (existingIndex >= 0) {
      const updated = [...existing];
      const day = updated[existingIndex];

      if (day.id) {
        // mark for destroy instead of removing
        updated[existingIndex] = {
          ...day,
          _destroy: !day._destroy, // toggle if needed
        };
      } else {
        // brand-new entry → remove from array
        updated.splice(existingIndex, 1);
      }

      setFieldValue('freight_day_settings_attributes', updated);
    } else {
      // not selected → add new day
      const updated = [
        ...existing,
        {
          day_of_week: dayId,
          delivery_fee: '',
          order_cut_off_time: '',
          minimum_order_value: '',
        },
      ];
      setFieldValue('freight_day_settings_attributes', updated);
    }
  };

  const initialData: FreightRule = editData
    ? {
        ...(() => {
          const { freight_day_settings, ...rest } = editData.freight_rule;
          return {
            ...rest,
            freight_day_settings_attributes: freight_day_settings || [],
          };
        })(),
      }
  : {
      name: '',
      delivery_type: '',
      is_default: false,
      default_delivery_fee: '',
      default_order_cut_off_time: '23:59',
      default_minimum_order_value: '',
      free_delivery_threshold: '',
      enable_free_delivery: false,
      custom_day_overrides: false,
      freight_day_settings_attributes: [],
    };

  return (
    <OverlayModal
      isOpen={isModalOpen}
      onClose={closeModal}
      animate={true}
      boxClassName={classes.modalBox}
      contentWrapClassName={classes.modalContent}
    >
      <div className={classes.title}>
        {recordId ? 'Edit Freight Rule' : 'Add New Freight Rule'}
      </div>
      <Formik
        initialValues={initialData}
        onSubmit={onFreightRuleSave}
        enableReinitialize={true}
        validationSchema={schema}
      >
        {({ submitForm, values, setFieldValue }) => (
        <Form>
          <div className={classes.formWrapper}>
            <div className={clsx(classes.ahalf, classes.bottomLine, classes.section1)}>
              <div className={clsx(classes.ahalfCol)}>
                <div>
                  <div className={classes.label}>Rule Name</div>
                  <FastField name='name'>
                    {(fieldProps: FieldProps) => <FormikInput {...fieldProps} placeholder='e.g. Monday delivery' height={48} />}
                  </FastField>
                </div>
                <div className={classes.defaultRule}>
                  <Field name='is_default'>
                    {(fieldProps: FieldProps) => <CommonSwitch {...fieldProps} skipSubmitOnChange={true} />}
                  </Field>
                  Set as Default rule
                </div>
              </div>
              <div className={clsx(classes.ahalfCol)}>
                <div>
                  <div className={classes.label}>Delivery Type</div>
                  <div className={classes.dropdown}>
                    <FastField name='delivery_type'>
                      {(fieldProps: FieldProps) => <FormikDropdown {...fieldProps}
                        placeholder='Select delivery type' options={deliveryTypeOptions} />}
                    </FastField>
                  </div>
                </div>
              </div>
            </div>

            <div className={classes.infoBox}>
              <div className={classes.sectionTitle}>DEFAULT DELIVERY PARAMETERS</div>
              <div className={classes.sectionDescription}>
                These settings apply to days without specific overrides.
              </div>
            </div>

            <div className={clsx(classes.ahalf, classes.bottomLine, classes.section2)}>
              <div className={clsx(classes.ahalfCol)}>
                <div>
                  <div className={classes.label}>Default Delivery Fee</div>
                  <FastField name='default_delivery_fee'>
                    {(fieldProps: FieldProps) => <FormikInput {...fieldProps}
                      allowZero={true}
                      inputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      placeholder='0.00' height={48} />}
                  </FastField>
                  <div className={classes.helpInfo}><SvgInfo /> The flat delivery charge for days without specific overrides</div>
                </div>
                <div>
                  <div className={classes.label}>Default Minimum Order Value</div>
                  <FastField name='default_minimum_order_value'>
                    {(fieldProps: FieldProps) => <FormikInput {...fieldProps}
                      allowZero={true}
                      inputProps={{
                        startAdornment: <InputAdornment position="start">$</InputAdornment>
                      }}
                      placeholder='0.00' height={48} />}
                  </FastField>
                  <div className={classes.helpInfo}><SvgInfo /> Minimum order amount for days without specific overrides</div>
                </div>
                <div className={classes.defaultRule}>
                  <Field name='enable_free_delivery'>
                    {(fieldProps: FieldProps) => <CommonSwitch {...fieldProps} skipSubmitOnChange={true} />}
                  </Field>
                  <span>Enable Free Delivery</span>
                </div>
              </div>
              <div className={clsx(classes.ahalfCol)}>
                <div>
                  <div className={classes.label}>Default Order Cut-off Time</div>
                  <div className={classes.cutoffTime}>
                    <FastField name='default_order_cut_off_time'>
                      {({ field, form }: FieldProps) => (
                        <CustomTimePicker
                          time={stringToDate(field.value)}
                          onSetTime={(date: Date | null) => {
                            const timeStr = dateToString(date);
                            form.setFieldValue(field.name, timeStr);
                          }}
                        />
                      )}
                    </FastField>
                  </div>
                  <div className={classes.helpInfo}><SvgInfo /> The daily cutoff time for days without specific overrides</div>
                </div>
                {values.enable_free_delivery && <div>
                  <div className={classes.label}>Free Delivery Threshold</div>
                  <div>
                    <FastField name='free_delivery_threshold'>
                      {(fieldProps: FieldProps) => <FormikInput {...fieldProps}
                        inputProps={{
                          startAdornment: <InputAdornment position="start">$</InputAdornment>
                        }}
                        placeholder='0.00' height={48} />}
                    </FastField>
                  </div>
                  <div className={classes.helpInfo}><SvgInfo /> Orders over this amount get free freight (delivery fee $0)</div>
                </div>}
              </div>
            </div>

            <div className={clsx(classes.bottomLine, classes.section2)}>
              <div className={classes.defaultRule}>
                <span className={classes.customText}>Custom Day Overrides</span>
                <Field name='custom_day_overrides'>
                  {(fieldProps: FieldProps) => <CommonSwitch  {...fieldProps} skipSubmitOnChange={true} />}
                </Field>
              </div>

              {values.custom_day_overrides && <div>
                <div className={classes.customDayInfo}>Set specific delivery parameters for individual days (overrides defaults).</div>
                <div className={clsx(classes.schedulesBox)}> 
                    {deliveryDays.map((day) => {
                      const isSelected = (values.freight_day_settings_attributes || []).some(
                        (d) => d.day_of_week === day.key && !d._destroy
                      );
                      return (
                        <Chip
                          key={`day-${day.key}`}
                          label={day.label.toUpperCase()}
                          clickable
                          className={clsx(classes.scheduleChip, isSelected && "chipSelected")}
                          variant={isSelected ? "outlined" : "default"}
                          color={isSelected ? "primary" : "default"}
                          onClick={() => handleChipClick(day.key, values, setFieldValue)}
                          deleteIcon={<Close className={classes.deleteIcon} />}
                          onDelete={
                            isSelected
                              ? () => handleChipClick(day.key, values, setFieldValue)
                              : undefined
                          }
                        />
                      );
                    })}
                </div>
                <div>
                  <FieldArray name="freight_day_settings_attributes">
                    {({ remove, replace }) => {
                      const dayOrder = deliveryDays.map((d) => d.key); // ["monday", "tuesday", ...]

                      const sortedDays = (values.freight_day_settings_attributes || [])
                        .map((day, i) => ({ ...day, originalIndex: i })) 
                        .filter((day) => !day._destroy)
                        .sort(
                          (a, b) =>
                            dayOrder.indexOf(a.day_of_week.toLowerCase()) -
                            dayOrder.indexOf(b.day_of_week.toLowerCase())
                        );

                      return <>
                        {sortedDays.map((day) => (
                          <DaySettings
                            key={day.day_of_week}
                            day={day.day_of_week}
                            index={day.originalIndex}
                            onDelete={() => {
                              if (day.id) {
                                replace(day.originalIndex, { ...day, _destroy: true });
                              } else {
                                remove(day.originalIndex);
                              }
                            }}
                            values={values}
                            setFieldValue={setFieldValue}
                          />
                        ))}
                      </>
                    }}
                  </FieldArray>
                </div>
              </div>}
            </div>
          </div>
          <div className={classes.buttonContainer}>
            <ThemedButton width={160} onClick={closeModal} title='Cancel' buttonStyle='secondary' />
            <ThemedButton width={160} onClick={submitForm} title='Save' buttonStyle='primary' />
          </div>
        </Form>
      )}
      </Formik>
    </OverlayModal>
  );
};
